import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { Date as DayLabel } from "@dashboard/components/Date/Date";
import { DetailPageLayout } from "@dashboard/components/Layouts/Detail";
import { type AtticLotDetailsFragment } from "@dashboard/graphql";
import { MONO_FONT_FAMILY } from "@dashboard/styles/monoFontFamily";
import { Box, Text } from "@saleor/macaw-ui-next";
import { type ReactNode } from "react";
import { useIntl } from "react-intl";

import { locationMessages, lotMessages, sectionMessages, sourceMessages } from "../../messages";
import { AtticInventoryDestinationIcon } from "../../topNav";
import { atticLotListPath } from "../../urls";
import { formatCost } from "../../utils";
import { AtticUnitsTable } from "../AtticUnitsTable/AtticUnitsTable";

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box display="flex" flexDirection="column" gap={1}>
    <Text size={2} color="default2">
      {label}
    </Text>
    <Text size={3}>{children}</Text>
  </Box>
);

export const AtticLotDetailsPage = ({ lot }: { lot: AtticLotDetailsFragment }) => {
  const intl = useIntl();
  const title = lot.reference || intl.formatMessage(lotMessages.untitled);

  return (
    <DetailPageLayout gridTemplateColumns={1}>
      <TopNav
        href={atticLotListPath}
        hrefIcon={<AtticInventoryDestinationIcon />}
        hrefTitle={intl.formatMessage(sectionMessages.lots)}
        title={title}
      />
      <DetailPageLayout.Content>
        <DashboardCard data-test-id="attic-lot-details">
          <DashboardCard.Content>
            <Box display="grid" gridTemplateColumns={{ mobile: 1, desktop: 3 }} gap={4}>
              <Row label={intl.formatMessage(lotMessages.source)}>
                {intl.formatMessage(sourceMessages[lot.source])}
              </Row>
              <Row label={intl.formatMessage(lotMessages.supplier)}>{lot.supplier}</Row>
              <Row label={intl.formatMessage(lotMessages.received)}>
                <DayLabel date={lot.receivedAt} plain />
              </Row>
              <Row label={intl.formatMessage(lotMessages.onHand)}>{lot.unitCount}</Row>
              <Row label={intl.formatMessage(lotMessages.cost)}>
                {formatCost(lot.totalCost, lot.currency, intl)}
              </Row>
              <Row label={intl.formatMessage(locationMessages.barcode)}>
                <span style={{ fontFamily: MONO_FONT_FAMILY }}>{lot.barcode}</span>
              </Row>
            </Box>
            {lot.notes && (
              <Box marginTop={4}>
                <Row label={intl.formatMessage(lotMessages.notes)}>{lot.notes}</Row>
              </Box>
            )}
          </DashboardCard.Content>
        </DashboardCard>
        <DashboardCard data-test-id="attic-lot-units-card">
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(lotMessages.unitsTitle)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content paddingX={0}>
            <AtticUnitsTable
              units={lot.units}
              show={{ sku: true, location: true }}
              emptyText={intl.formatMessage(lotMessages.noUnits)}
            />
          </DashboardCard.Content>
        </DashboardCard>
      </DetailPageLayout.Content>
    </DetailPageLayout>
  );
};

export default AtticLotDetailsPage;
